import axios from "axios"
import { IFinalEvent } from "types/event"
import { ItimeSlot } from "types/ticket"

export type FetchEventPayload = IFinalEvent | { event: IFinalEvent }

export interface FetchEventResponse {
    payload: FetchEventPayload
    message?: string
    status?: number
}

function isMongoObjectId(value: string): boolean {
    return /^[a-fA-F0-9]{24}$/.test(value)
}

export function normalizeEventPayload(payload?: FetchEventPayload): IFinalEvent | undefined {
    if (!payload) {
        return undefined
    }

    if ("event" in payload) {
        return payload.event
    }

    return payload
}

export function normalizeEventTimeSlots(event?: IFinalEvent): ItimeSlot[] {
    if (!event?.time_slots?.length) {
        return []
    }

    const availableTickets = new Map(
        event.availableTickets.map((ticket) => [ticket._id, ticket])
    )

    return event.time_slots.map((slot) => {
        const slotMeta = slot as unknown as {
            status?: string
            is_special_day?: boolean
            allow_using_coupon?: boolean
            is_bundle_time?: boolean
            archive?: boolean
            is_selling_time_open?: boolean
        }

        return {
            _id: slot._id,
            start_slot: String(slot.start_slot),
            end_slot: String(slot.end_slot),
            event_id: slot.event_id,
            status: typeof slotMeta.status === "string" ? slotMeta.status : "active",
            is_special_day:
                typeof slotMeta.is_special_day === "boolean"
                    ? slotMeta.is_special_day
                    : false,
            allow_using_coupon:
                typeof slotMeta.allow_using_coupon === "boolean"
                    ? slotMeta.allow_using_coupon
                    : false,
            types: (slot.types || []).map((type) => {
                const typeMeta = type as unknown as {
                    wait_list?: number
                    original_limit?: number
                }

            const typeId =
                typeof type.type_id === "string"
                    ? type.type_id
                    : type.type_id?._id || ""
            const ticketDetails =
                availableTickets.get(typeId) ||
                (typeof type.type_id === "string" ? undefined : type.type_id)

            return {
                wait_list:
                    typeof typeMeta.wait_list === "number"
                        ? typeMeta.wait_list
                        : 0,
                ar_description: ticketDetails?.ar_description ?? "",
                en_description: ticketDetails?.en_description ?? "",
                en_title: ticketDetails?.en_title ?? "",
                ar_title: ticketDetails?.ar_title ?? "",
                color: ticketDetails?.color ?? "#ffffff",
                discount: ticketDetails?.discount ?? {
                    current_value: 0,
                    previous_value: 0,
                },
                limit: type.limit,
                on_hold: type.on_hold,
                original_limit:
                    typeof typeMeta.original_limit === "number"
                        ? typeMeta.original_limit
                        : type.limit,
                _id: type._id,
                min: ticketDetails?.min ?? 1,
                max: ticketDetails?.max ?? 0,
                must_select_seat: ticketDetails?.must_select_seat ?? false,
                price: ticketDetails?.price ?? 0,
                type_id: typeId,
            }
            }),
            is_bundle_time:
                typeof slotMeta.is_bundle_time === "boolean"
                    ? slotMeta.is_bundle_time
                    : false,
            archive:
                typeof slotMeta.archive === "boolean" ? slotMeta.archive : false,
            __v: slot.__v,
            createdAt: String(slot.createdAt),
            updatedAt: String(slot.updatedAt),
            is_selling_time_open:
                typeof slotMeta.is_selling_time_open === "boolean"
                    ? slotMeta.is_selling_time_open
                    : undefined,
            seats_season_id: slot.seats_season_id,
        }
    })
}

import { withServerCache } from "utils/serverCache"

/**
 * Fetches event data from the API
 * @param eventId - The ID of the event to fetch
 * @returns The event data
 */
export default async function fetchEvent(eventId: string): Promise<FetchEventResponse> {
    try {
        if (!eventId) {
            throw new Error("EVENT_ID_REQUIRED")
        }

        return withServerCache(
            `event-${eventId}`,
            async () => {
                const apiUrl = process.env.NEXT_PUBLIC_API_URL_V2
                const url = isMongoObjectId(eventId)
                    ? `${apiUrl}events/${eventId}`
                    : `${apiUrl}events/slug/${eventId}`
                const { data } = await axios.get(url)

                if (
                    data.status === 400 ||
                    (data.status === 404 && data.message === "couldnt find event")
                ) {
                    throw new Error("EVENT_NOT_FOUND")
                }

                return data
            },
            60 * 1000 // 1 minute TTL
        )
    } catch (error) {
        if (
            axios.isAxiosError(error) &&
            (error.response?.status === 400 || error.response?.status === 404)
        ) {
            throw new Error("EVENT_NOT_FOUND")
        }
        throw error
    }
}

